import { VideoPageModule } from './pages/video/video.module';
import { SelectModalPageModule } from './pages/select-modal/select-modal.module';
import { CitiesService } from './services/cities/cities.service';
import { UserDetailPageModule } from './pages/users/user-detail/user-detail.module';
import { environment } from './../environments/environment';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { HttpClientModule } from '@angular/common/http';
import * as Sentry from "@sentry/angular";
import { Router } from "@angular/router";
import { ClipboardModule } from 'ngx-clipboard';

// firebase
import { AngularFireModule } from '@angular/fire';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFirestoreModule } from '@angular/fire/firestore';
import { AngularFireStorageModule } from '@angular/fire/storage';
import { AngularFirePerformanceModule } from '@angular/fire/performance';
import { AngularFireMessagingModule } from '@angular/fire/messaging';
import { AngularFireAnalyticsModule, ScreenTrackingService, UserTrackingService, CONFIG } from '@angular/fire/analytics';
import { AngularFireFunctionsModule, REGION } from '@angular/fire/functions';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HelperService } from './services/helper/helper.service';
import { OpenModalGuardService } from './services/open-modal-guard/open-modal-guard.service';
import { MapPageModule } from './pages/map/map.module';
import { LoginPageModule } from './pages/login/login.module';
import { UserDataService } from './services/user-data/user-data.service';
import { FunnelGuardService } from './services/funnel-guard/funnel-guard.service';
import { ConfirmPageModule } from './pages/confirm/confirm.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ErrorMonitoringService } from './services/error-monitoring/error-monitoring.service';
import { ServiceService } from './services/service/service.service';
import { AnalyticsService } from './services/analytics/analytics.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { WalletService } from './services/wallet/wallet.service';
import { ComponentsModule } from './components/components.module';
import { DebtorGuardService } from './services/debtor-guard/debtor-guard.service';

@NgModule({
  declarations: [
    AppComponent
  ],
  entryComponents: [],
  imports: [
    BrowserModule,
    IonicModule.forRoot({mode: 'md'}),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    AngularFirestoreModule,
    AngularFireStorageModule,
    AngularFirePerformanceModule,
    AngularFireFunctionsModule,
    AngularFireMessagingModule,
    AngularFireAnalyticsModule,
    AppRoutingModule,
    MapPageModule,
    LoginPageModule,
    HttpClientModule,
    ConfirmPageModule,
    ServiceWorkerModule.register('combined-sw.js', { enabled: environment.production || environment.test }),
    BrowserAnimationsModule,
    ComponentsModule,
    UserDetailPageModule,
    SelectModalPageModule,
    ClipboardModule,
    VideoPageModule
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true,
        showDialog: environment.production && environment.test ? true : false
      }),
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: REGION, useValue: 'us-east1' },
    { provide: CONFIG, useValue: {
        APP_NAME: 'servicios',
        APP_VERSION: environment.version
      }
    },
    // configura tarceservice de Sentry
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // inicializa el servici de sentry
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    HelperService,
    OpenModalGuardService,
    UserDataService,
    FunnelGuardService,
    ServiceService,
    AnalyticsService,
    WalletService,
    CitiesService,
    DebtorGuardService,
    ScreenTrackingService,
    UserTrackingService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {

  constructor(
  ) {
    // Carga el script de maps
    const mapsScript = document.createElement('script');
    mapsScript.innerHTML = '';
    mapsScript.src = `https://maps.google.com/maps/api/js?key=${environment.Maps_API_KEY}&libraries=places`;
    mapsScript.async = true;
    mapsScript.defer = true;
    document.body.appendChild(mapsScript);
    // si es production
    if (environment.production && !environment.test) {
      // Carga tag manager
      const tagManagerScript = document.createElement('script');
      tagManagerScript.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-KTSJ36C');
      `;
      document.head.appendChild(tagManagerScript);
      const tagManagerNoScript = document.createElement('noscript');
      tagManagerNoScript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTSJ36C"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `;
      document.body.appendChild(tagManagerNoScript);
      // Carga el script de analytics
      const analyticsScript = document.createElement('script');
      analyticsScript.innerHTML = '';
      analyticsScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsTrackingCode}`;
      analyticsScript.async = true;
      document.head.appendChild(analyticsScript);
      // carga la config de analytics
      const analyticsConfigScript = document.createElement('script');
      analyticsConfigScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
      `;
      document.head.appendChild(analyticsConfigScript);
      // carga el pixel de facebook
      const pixelScript = document.createElement('script');
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', ${environment.facebookPixel});
      `;
      document.head.appendChild(pixelScript);
      const pixelNoScript = document.createElement('noscript');
      pixelNoScript.innerHTML = `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=${environment.facebookPixel}&ev=PageView&noscript=1"
      `;
      document.head.appendChild(pixelNoScript);
    }
  }
}
